class CartDiscount extends HTMLElement {
  // Promo result wording — the one source for the inline message under the
  // input and the note under a pill.
  static get MESSAGES() {
    return {
      partial: 'Discount applied. Excludes sale items.',
      excluded: 'This code excludes sale items.',
      invalid: "This code isn't valid.",
      applied: 'Discount applied.',
    };
  }

  // sessionStorage marker that carries a successful apply across the page
  // reload cart-new.js performs (see announcePendingResult)
  static get ANNOUNCE_KEY() {
    return 'cartPromoAnnounce';
  }

  constructor() {
    super();
    this.activeFetch = null;
    this._onCartChangeListener = this.handleCartChange.bind(this);
    this.init();
  }

  init() {
    this.bindEvents();
    // Hide any existing errors on initialization
    this.hideErrors();
    // cart-new.js can swap this component's markup back to the collapsed
    // server render and then call init() — keep the panel open if a pill note
    // was showing, instead of closing and re-opening it
    if (this._notesShowing) this.openPanel();
    // Initialize with current cart data after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeWithCartData();
    }, 100);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this._onCartChangeListener) {
      document.removeEventListener('cart:change', this._onCartChangeListener);
      document.removeEventListener('cart:updated', this._onCartChangeListener);
    }
  }

  bindEvents() {
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.applyDiscount.bind(this));
    } else {
      console.log('No form found in cart-discount component');
    }

    // Bind input event to clear errors when user starts typing
    const discountInput = this.querySelector('input[name="discount"]');
    if (discountInput) {
      discountInput.addEventListener('input', this.hideErrors.bind(this));
    }

    // Bind remove buttons for existing discount pills
    this.bindRemoveButtons();
    
    // Listen for cart changes (incl. external line-item removal) to re-validate codes
    document.addEventListener('cart:change', this._onCartChangeListener);
    document.addEventListener('cart:updated', this._onCartChangeListener);
  }

  bindRemoveButtons() {
    const removeButtons = this.querySelectorAll('.cart-discount__pill-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', this.removeDiscount.bind(this));
    });
  }

  async initializeWithCartData() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        await this.updateComponentWithCartData(cartData);

        // Initialize discount amounts for existing elements
        if (cartData.cart_level_discount_applications) {
          this.updateDiscountApplications(cartData.cart_level_discount_applications);
        }

        this.announcePendingResult(cartData);
      } else {
        console.error('Failed to fetch cart data for initialization');
      }
    } catch (error) {
      console.error('Error initializing with cart data:', error);
    }
  }

  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }

    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  async applyDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;


    const abortController = this.createAbortController();

    try {
      const existingDiscounts = this.getExistingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        // Code already exists, show a message or just return
        console.log('Discount code already applied');
        discountCode.value = '';
        return;
      }

      this.hideErrors();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 422) {
          // Shopify rejected the request itself (malformed payload). A bad code
          // never lands here — that comes back 200 + applicable:false below.
          this.showPromoMessage({ kind: 'error', text: CartDiscount.MESSAGES.invalid });
          this.publishDiscountError(discountCodeValue, CartDiscount.MESSAGES.invalid, 'invalid');
          discountCode.value = '';
          return;
        }
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      
      console.log('Cart update response:', data);

      // Check if discount was applied successfully
      let discountApplied = false;
      
      // Check discount_codes array
      if (data.discount_codes && data.discount_codes.length > 0) {
        console.log('Checking discount_codes:', data.discount_codes);
        const appliedDiscount = data.discount_codes.find(discount =>
          this.sameCode(discount.code, discountCodeValue) && discount.applicable === true
        );
        if (appliedDiscount) {
          console.log('Found in discount_codes:', appliedDiscount);
          discountApplied = true;
        }
      }
      
      // Check discount_applications array
      if (data.discount_applications && data.discount_applications.length > 0) {
        console.log('Checking discount_applications:', data.discount_applications);
        const appliedDiscount = data.discount_applications.find(discount =>
          this.sameCode(discount.title, discountCodeValue)
        );
        if (appliedDiscount) {
          console.log('Found in discount_applications:', appliedDiscount);
          discountApplied = true;
        }
      }
      
      // Check cart_level_discount_applications array
      if (data.cart_level_discount_applications && data.cart_level_discount_applications.length > 0) {
        console.log('Checking cart_level_discount_applications:', data.cart_level_discount_applications);
        const appliedDiscount = data.cart_level_discount_applications.find(discount =>
          this.sameCode(discount.title, discountCodeValue)
        );
        if (appliedDiscount) {
          console.log('Found in cart_level_discount_applications:', appliedDiscount);
          discountApplied = true;
        }
      }

      console.log('Final discount applied check:', discountApplied);
      
      if (!discountApplied) {
        console.log('Discount not applied, showing error');
        // Shopify answers 200 + applicable:false for BOTH a nonexistent code and a
        // valid code excluded from the items in the cart, with no reason attached.
        // getPromoMessage words it from what the cart holds: every line tagged
        // CODES-DISABLED reads as "excludes sale items", anything else as the
        // generic invalid-code message.
        const promoMessage = this.getPromoMessage(discountCodeValue, data, false);
        this.showPromoMessage(promoMessage);
        this.publishDiscountError(discountCodeValue, promoMessage.text, 'rejected');
        discountCode.value = '';
        return;
      }
      
      console.log('Discount applied successfully');
      
      // Clear the input
      discountCode.value = '';
      
      // Get updated cart data and update component
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();

        // Collapse the promo panel and let its height animation finish BEFORE
        // updating the component (the pill injection would otherwise grow the
        // open panel for a frame) and before dispatching cart:change —
        // cart-new.js reloads the page on discount:applied, which would cut
        // the collapse to an instant jump
        const promoDetails = this.querySelector('details');
        if (promoDetails && promoDetails.hasAttribute('open')) {
          promoDetails.removeAttribute('open');
          await new Promise(resolve => setTimeout(resolve, 250));
        }

        // The page is about to reload: don't let a pill note re-open the panel
        // that was just animated shut, and leave a one-shot marker so the
        // result is announced to screen readers after the reload. The flag
        // clears itself in case the reload never comes.
        this._reloadPending = true;
        setTimeout(() => { this._reloadPending = false; }, 3000);
        try {
          sessionStorage.setItem(CartDiscount.ANNOUNCE_KEY, JSON.stringify({ code: discountCodeValue, at: Date.now() }));
        } catch (e) {
          // storage unavailable (private mode) — the pill note still shows the result
        }

        await this.updateComponentWithCartData(cartData);

        // Update discount amounts immediately after successful application
        setTimeout(() => {
          this.updateDiscountAmounts();
        }, 100);

        // Trigger cart change event to update other components
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'discount:applied',
            cart: cartData
          }
        }));
        
        // Trigger cart updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        // Safe refresh after discount application
        setTimeout(() => {
          if (typeof safeInitQuantityControls === 'function') {
            safeInitQuantityControls();
          } else if (typeof initQuantityControls === 'function') {
            initQuantityControls();
          }
        }, 500);

      } else {
        console.error('Failed to fetch updated cart data');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error applying discount:', error);
        // Show error for network errors or invalid discount codes
        this.showPromoMessage({ kind: 'error', text: CartDiscount.MESSAGES.invalid });
        this.publishDiscountError(discountCodeValue, CartDiscount.MESSAGES.invalid, 'error');
        discountCode.value = '';
      }
    } finally {
      this.activeFetch = null;
    }
  }

  publishDiscountError(code, message, reason) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      const result = analytics.publish('mm_cart_discount_error', { code: code, message: message, reason: reason });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (e) {}
  }

  async removeDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const pill = event.target.closest('.cart-discount__pill');
    if (!pill) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;


    // Keep every ACTIVE code except the one being removed. A stale code (still
    // attached, applicable:false) isn't in the active list, so this also
    // resolves to "the active codes unchanged" — which is exactly the update
    // that drops it, since Shopify replaces the cart's code set with the list
    // posted here.
    const existingDiscounts = this.getExistingDiscounts().filter(code => code !== discountCode);

    const abortController = this.createAbortController();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      // Get updated cart data to refresh the component
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        await this.updateComponentWithCartData(cartData);
        
        // Trigger cart change event to update other components
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'discount:removed',
            cart: cartData
          }
        }));
        
        // Trigger cart updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        // Safe refresh after discount removal
        setTimeout(() => {
          if (typeof safeInitQuantityControls === 'function') {
            safeInitQuantityControls();
          } else if (typeof initQuantityControls === 'function') {
            initQuantityControls();
          }
        }, 500);
      } else {
        console.error('Failed to fetch updated cart data after removal');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error removing discount:', error);
      }
    } finally {
      this.activeFetch = null;
    }
  }

  async updateComponentWithCartData(cartData) {
    try {
      
      // Save cart data globally for access in other methods
      window.cartData = cartData;
      
      // Try different ways to get discount codes
      let discountCodes = [];
      
      // Method 1: Try discount_applications (only applicable ones)
      if (cartData.discount_applications && Array.isArray(cartData.discount_applications)) {
        discountCodes = cartData.discount_applications
          .filter(app => app.applicable !== false) // Only include applicable discounts
          .map(app => app.title);
      }
      
      // Method 2: Try discount_codes (only if no codes found yet and only applicable ones)
      if (discountCodes.length === 0 && cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
        discountCodes = cartData.discount_codes
          .filter(code => {
            if (typeof code === 'object') {
              return code.applicable !== false; // Only include applicable discounts
            }
            return true; // If it's a string, assume it's applicable
          })
          .map(code => {
            if (typeof code === 'object' && code.code) {
              return code.code;
            }
            return code;
          });
      }
      
      // Method 3: Try cart_level_discount_applications (only if no codes found yet)
      if (discountCodes.length === 0 && cartData.cart_level_discount_applications && Array.isArray(cartData.cart_level_discount_applications)) {
        discountCodes = cartData.cart_level_discount_applications
          .filter(app => app.applicable !== false) // Only include applicable discounts
          .map(app => app.title);
      }
      
      // Method 4: Check if there are any discount-related properties
      const discountKeys = Object.keys(cartData).filter(key => key.includes('discount'));
      
      console.log('Found discount codes:', discountCodes);
      console.log('Cart data discount keys:', discountKeys);
      
      // Validate and filter out any invalid codes
      const validDiscountCodes = await this.validateDiscountCodes(discountCodes, cartData);
      console.log('Valid discount codes:', validDiscountCodes);
      
      // If we have invalid codes, remove them from the cart
      if (validDiscountCodes.length < discountCodes.length) {
        console.log('Removing invalid discount codes from cart');
        await this.removeInvalidDiscounts(discountCodes, validDiscountCodes);
      }

      // Codes still attached to the cart but producing no discount. Verified
      // against the live store: removing a code's only eligible line leaves
      // discount_codes as [{code:'LABOR20', applicable:false}] — Shopify keeps
      // the code and silently re-activates it if an eligible line comes back.
      // They render as removable (inactive) pills so the shopper can see and
      // drop them; the header derives from the active list only.
      const staleDiscountCodes = Array.isArray(cartData.discount_codes)
        ? cartData.discount_codes
            .filter(dc => dc && typeof dc === 'object' && dc.code && dc.applicable === false)
            .map(dc => dc.code)
            .filter(code => !validDiscountCodes.includes(code))
        : [];

      this.syncAppliedState(validDiscountCodes);
      this.updateDiscountPills(validDiscountCodes, staleDiscountCodes, cartData);

      // Update cart totals in cart drawer
      this.updateCartTotals(cartData);

      // Update cart count
      this.updateCartCount(cartData.item_count);
      
      // Update discount amounts with fresh cart data
      setTimeout(() => {
        this.updateDiscountAmounts();
      }, 50);
      


    } catch (error) {
      console.error('Error updating component:', error);
    }
  }

  // The promo row header (check icon + "Promo code: X applied" / "Have a promo
  // code?") is server-rendered with both states in the markup. Re-derive which
  // one shows from cart truth — called on every updateComponentWithCartData,
  // which runs after apply/remove and after any cart mutation settles
  // (cart-new.js dispatches cart:change with the fresh /cart.js payload). A
  // code that stops applying, e.g. its only eligible line was removed, drops
  // the applied header here instead of surviving until a hard refresh.
  syncAppliedState(activeCodes) {
    const summary = this.querySelector('.accordion__summary');
    if (!summary) return;

    const applied = activeCodes.length > 0;
    const check = summary.querySelector('.cart-new__discount-check');
    const appliedLabel = summary.querySelector('.cart-new__discount-label--applied');
    const emptyLabel = summary.querySelector('.cart-new__discount-label--empty');

    // SVG elements have no `hidden` IDL property — toggle the attribute
    if (check) {
      if (applied) check.removeAttribute('hidden');
      else check.setAttribute('hidden', '');
    }
    if (appliedLabel) {
      appliedLabel.hidden = !applied;
      const codeEl = appliedLabel.querySelector('strong');
      if (codeEl && applied) codeEl.textContent = activeCodes[0];
    }
    if (emptyLabel) emptyLabel.hidden = applied;
  }

  updateDiscountPills(discountCodes, staleCodes = [], cartData = window.cartData) {
    const codesList = this.querySelector('.cart-discount__codes');
    if (!codesList) {
      console.log('No cart-discount__codes list found');
      return;
    }

    // Clear existing pills
    codesList.innerHTML = '';

    // Add new pills
    discountCodes.forEach(code => {
      codesList.appendChild(this.buildPill(code, false, this.getPromoMessage(code, cartData, true)));
    });

    // Stale codes: attached to the cart, producing no discount. Rendered
    // after the active ones, muted, with a note and the same remove button —
    // the shopper must always be able to drop a code, stale or not.
    staleCodes.forEach(code => {
      codesList.appendChild(this.buildPill(code, true, this.getPromoMessage(code, cartData, false)));
    });

    // A stale pill's note now says what the inline message said when the code
    // was rejected — drop the inline copy so the text isn't shown twice
    if (staleCodes.length > 0) this.hideErrors();

    // Notes sit inside the collapsible panel, which is closed by default
    // (section setting show_promo). Open it so a note is never hidden — except
    // right after a successful apply, when applyDiscount has just animated the
    // panel shut ahead of the reload.
    this._notesShowing = !!codesList.querySelector('.cart-discount__pill-note');
    if (this._notesShowing && !this._reloadPending) this.openPanel();

    // Re-bind remove buttons
    this.bindRemoveButtons();

    // Update discount amounts after a short delay to ensure cart data is fully updated
    setTimeout(() => {
      this.updateDiscountAmounts();
    }, 100);
  }

  // message: the { kind, text } getPromoMessage returned for this code, or null
  buildPill(code, inactive, message = null) {
    const pill = document.createElement('li');
    pill.className = 'cart-discount__pill'
      + (inactive ? ' cart-discount__pill--inactive' : '')
      + (message ? ' cart-discount__pill--has-note' : '');
    pill.dataset.discountCode = code;
    // The reason lives in the note below, so the label only states the state
    pill.setAttribute('aria-label', inactive
      ? `Discount ${code} not applied`
      : `Discount ${code} applied`);

    // Find discount amount for this code
    let discountAmount = '';
    if (!inactive && window.cartData && window.cartData.cart_level_discount_applications) {
      const discountApp = window.cartData.cart_level_discount_applications.find(app => app.title === code);
      if (discountApp) {
        discountAmount = ` -${this.formatMoney(discountApp.total_allocated_amount)}`;
      }
    }

    pill.innerHTML = `
      
      <p class="cart-discount__pill-code">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M3.75542 0.000609069C4.03906 -0.00917218 4.31403 0.0992153 4.51472 0.2999L9.07509 4.86028C9.47497 5.26014 9.47497 5.90845 9.07509 6.30833L6.30833 9.07511C5.90845 9.47497 5.26014 9.47497 4.86028 9.07509L0.2999 4.51472C0.0992153 4.31403 -0.00917218 4.03906 0.000609069 3.75541L0.0928356 1.08086C0.111379 0.543056 0.543059 0.111379 1.08086 0.0928341L3.75542 0.000609069ZM4.07278 0.741842C3.99459 0.663654 3.88747 0.621426 3.77695 0.625237L1.1024 0.717464C0.892872 0.724689 0.724689 0.89287 0.717464 1.1024L0.625237 3.77695C0.621426 3.88745 0.663654 3.99459 0.741842 4.07278L5.30222 8.63316C5.45801 8.78895 5.71059 8.78895 5.86637 8.63316L8.63316 5.86637C8.78895 5.71059 8.78895 5.45801 8.63316 5.30222L4.07278 0.741842ZM1.96773 1.96773C2.29814 1.63733 2.83384 1.63733 3.16425 1.96773C3.49467 2.29814 3.49467 2.83384 3.16425 3.16425C2.83384 3.49467 2.29814 3.49467 1.96773 3.16425C1.63733 2.83384 1.63733 2.29814 1.96773 1.96773ZM2.72231 2.40969C2.63598 2.32334 2.49602 2.32334 2.40969 2.40969C2.32334 2.49602 2.32334 2.63598 2.40969 2.72231C2.49602 2.80864 2.63598 2.80864 2.72231 2.72231C2.80864 2.63598 2.80864 2.49602 2.72231 2.40969Z" fill="#505050"/>
          </svg>
          ${code}
          <button
          type="button"
          class="cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled"
          aria-label="Remove discount ${code}"
          >
          ✕
          </button>
      </p>
      
      
      <span class="text-subdued discount-amount">${discountAmount}</span>
      ${message ? `<span class="cart-discount__pill-note cart-discount__pill-note--${message.kind}">${message.text}</span>` : ''}
    `;

    return pill;
  }

  // Codes compare case-insensitively: Shopify matches them that way, so
  // "sub15" typed against "SUB15" returned must count as the same code
  sameCode(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  // product id -> true/false, read from the cart rows Liquid rendered
  // (cart-new-items.liquid stamps data-codes-disabled from the CODES-DISABLED
  // product tag). A product with no row is simply absent from the map.
  getExcludedProducts() {
    const excluded = new Map();
    document.querySelectorAll('tr[data-line-key][data-codes-disabled]').forEach(row => {
      excluded.set(String(row.getAttribute('data-product-id')), row.getAttribute('data-codes-disabled') === 'true');
    });
    return excluded;
  }

  // The single decision behind every promo message. Returns { kind, text } or
  // null when there is nothing to add.
  //   applicable + a line got the code + a CODES-DISABLED line did not -> info
  //   not applicable + every line is CODES-DISABLED                    -> error (excludes sale items)
  //   not applicable, anything else (typo, expired, ...)               -> error (generic)
  // Order-level codes carry no line allocations, so an applicable one returns
  // null and behaves as before.
  getPromoMessage(code, cartData, applicable) {
    const items = cartData && Array.isArray(cartData.items) ? cartData.items : [];
    const excludedProducts = this.getExcludedProducts();
    const isExcluded = item => excludedProducts.get(String(item.product_id)) === true;

    if (!applicable) {
      const allExcluded = items.length > 0 && items.every(isExcluded);
      return {
        kind: 'error',
        text: allExcluded ? CartDiscount.MESSAGES.excluded : CartDiscount.MESSAGES.invalid,
      };
    }

    // discount codes only — an automatic discount that happens to share the
    // title must not trigger the note
    const gotCode = item => Array.isArray(item.line_level_discount_allocations)
      && item.line_level_discount_allocations.some(allocation => {
        const application = allocation && allocation.discount_application;
        return application
          && (!application.type || application.type === 'discount_code')
          && this.sameCode(application.title, code);
      });

    if (!items.some(gotCode)) return null;

    const excludedLineSkipped = items.some(item => isExcluded(item) && !gotCode(item));
    return excludedLineSkipped ? { kind: 'info', text: CartDiscount.MESSAGES.partial } : null;
  }

  openPanel() {
    const details = this.querySelector('details');
    if (details && !details.hasAttribute('open')) details.setAttribute('open', '');
  }

  // After a successful apply cart-new.js reloads the page, so the result can
  // only be announced on the far side of the reload. applyDiscount leaves a
  // one-shot marker; this reads it once and speaks the same message the pill
  // note shows. Visually hidden — the pill already carries the visible text.
  announcePendingResult(cartData) {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(CartDiscount.ANNOUNCE_KEY) || 'null');
      sessionStorage.removeItem(CartDiscount.ANNOUNCE_KEY);
    } catch (e) {
      return;
    }
    if (!pending || !pending.code || Date.now() - pending.at > 30000) return;
    if (!this.getExistingDiscounts().some(code => this.sameCode(code, pending.code))) return;

    const message = this.getPromoMessage(pending.code, cartData, true)
      || { kind: 'info', text: CartDiscount.MESSAGES.applied };
    this.showPromoMessage(message, true);
  }

  updateDiscountAmounts() {
    if (!window.cartData || !window.cartData.cart_level_discount_applications) {
      return;
    }

    const discountPills = this.querySelectorAll('.cart-discount__pill');
    discountPills.forEach(pill => {
      const code = pill.dataset.discountCode;
      const discountAmountElement = pill.querySelector('.discount-amount');
      
      if (code && discountAmountElement) {
        const discountApp = window.cartData.cart_level_discount_applications.find(app => app.title === code);
        if (discountApp) {
          discountAmountElement.textContent = ` -${this.formatMoney(discountApp.total_allocated_amount)}`;
        }
      }
    });
  }

  updateCartTotals(cartData) {
    // Update subtotal - find by looking for elements containing "Subtotal"
    const subtotalElements = document.querySelectorAll('.cart-drawer .h-stack');
    subtotalElements.forEach(element => {
      const textElement = element.querySelector('.text-subdued');
      if (textElement && textElement.textContent.includes('Subtotal')) {
        const priceElement = element.querySelector('.text-subdued:last-child');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(cartData.items_subtotal_price);
        }
      }
    });

    // Update total - find by looking for elements containing "Total"
    const totalElements = document.querySelectorAll('.cart-drawer .h-stack');
    totalElements.forEach(element => {
      const textElement = element.querySelector('.h5');
      if (textElement && textElement.textContent.includes('Total')) {
        const priceElement = element.querySelector('.h5:last-child');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(cartData.total_price);
        }
      }
    });

    // Update discount applications
    this.updateDiscountApplications(cartData.discount_applications || []);
  }

  updateDiscountApplications(discountApplications) {
    // Find the discount applications section
    const discountSection = document.querySelector('.cart-drawer');
    if (!discountSection) return;

    // Update existing discount amount elements in the component
    const discountAmountElements = this.querySelectorAll('.discount-amount');
    discountAmountElements.forEach((element, index) => {
      if (discountApplications[index]) {
        element.textContent = `-${this.formatMoney(discountApplications[index].total_allocated_amount)}`;
      } else {
        element.textContent = '';
      }
    });

    // Also update discount amounts in cart drawer
    const cartDiscountAmountElements = discountSection.querySelectorAll('.discount-amount');
    cartDiscountAmountElements.forEach((element, index) => {
      if (discountApplications[index]) {
        element.textContent = `-${this.formatMoney(discountApplications[index].total_allocated_amount)}`;
      } else {
        element.textContent = '';
      }
    });

    // Remove existing discount applications
    const existingDiscounts = discountSection.querySelectorAll('.h-stack');
    existingDiscounts.forEach(el => {
      const badge = el.querySelector('.badge');
      if (badge && (el.textContent.includes('discount') || el.textContent.includes('🎫'))) {
        el.remove();
      }
    });
  }

  updateCartCount(itemCount) {
    // Update cart count in header if it exists
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = itemCount;
      element.style.display = itemCount > 0 ? 'block' : 'none';
    });
  }

  // Same formatter as cart-new.js: themeVariables.settings.moneyFormat is the
  // active presentment currency's format, so promo totals match the Liquid
  // `| money_without_trailing_zeros` output (no "CA$", exact .00 dropped).
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat)
      || '${{amount_no_decimals}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    const withDelimiters = (number, precision, thousands, decimal) => {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] && parts[1] !== '00' ? decimal + parts[1] : '');
    };
    let value;
    switch (placeholder) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      case 'amount_no_decimals_with_apostrophe_separator': value = withDelimiters(cents, 0, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  hideErrors() {
    // Hide the main error container
    const errorContainer = this.querySelector('.cart-discount__error');
    if (errorContainer) {
      errorContainer.classList.add('hidden');
      errorContainer.classList.remove('sr-only', 'cart-discount__error--info');
    }

    // Hide all error text elements
    const errorElements = this.querySelectorAll('.cart-discount__error-text');
    errorElements.forEach(element => {
      element.classList.add('hidden');
    });
  }

  // Shows a { kind, text } promo message in the live region under the input.
  // kind 'error' is red, 'info' is neutral. visuallyHidden keeps it
  // screen-reader-only (used when a pill note already shows the same text).
  showPromoMessage(message, visuallyHidden = false) {
    if (!message) return;

    const errorContainer = this.querySelector('.cart-discount__error');
    // Single message: the first error-text element in this component. (The
    // cart page ships one; the disabled drawer markup still lists two.)
    const errorElement = this.querySelector('.cart-discount__error-text');
    if (!errorContainer || !errorElement) {
      console.log('Promo message elements not found');
      return;
    }

    errorContainer.classList.toggle('cart-discount__error--info', message.kind === 'info');
    errorContainer.classList.toggle('sr-only', visuallyHidden);
    errorContainer.classList.remove('hidden');
    errorElement.classList.remove('hidden');

    // aria-live announces a content change, not an un-hiding — display-toggle
    // updates are dropped by some screen readers. Write the text after the
    // reveal has been processed so the announcement fires from an actual
    // content change.
    errorElement.textContent = '';
    setTimeout(() => {
      errorElement.textContent = message.text;
    }, 100);
  }

  async validateDiscountCodes(discountCodes, cartData) {
    const validCodes = [];
    
    for (const code of discountCodes) {
      // Check if the code is actually applied in the cart data
      let isValid = false;
      
      // Check discount_applications
      if (cartData.discount_applications && Array.isArray(cartData.discount_applications)) {
        const discountApp = cartData.discount_applications.find(app => app.title === code);
        if (discountApp && discountApp.applicable !== false) {
          isValid = true;
        }
      }
      
      // Check cart_level_discount_applications
      if (!isValid && cartData.cart_level_discount_applications && Array.isArray(cartData.cart_level_discount_applications)) {
        const discountApp = cartData.cart_level_discount_applications.find(app => app.title === code);
        if (discountApp && discountApp.applicable !== false) {
          isValid = true;
        }
      }
      
      // Check discount_codes
      if (!isValid && cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
        const discountCode = cartData.discount_codes.find(dc => {
          if (typeof dc === 'object' && dc.code) {
            return dc.code === code && dc.applicable !== false;
          }
          return dc === code;
        });
        if (discountCode) {
          isValid = true;
        }
      }
      
      if (isValid) {
        validCodes.push(code);
      } else {
        console.log(`Invalid discount code removed: ${code}`);
      }
    }
    
    return validCodes;
  }

  async removeInvalidDiscounts(allCodes, validCodes) {
    try {
      // Create a comma-separated string of only valid codes
      const validCodesString = validCodes.join(',');
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: validCodesString,
        }),
      });

      if (!response.ok) {
        console.error('Failed to remove invalid discount codes');
        return;
      }

      console.log('Successfully removed invalid discount codes');
      
      // Refresh cart data
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        await this.updateComponentWithCartData(cartData);
      }
    } catch (error) {
      console.error('Error removing invalid discount codes:', error);
    }
  }

  handleCartChange(event) {
    if (!(event.detail && event.detail.cart)) return;

    window.cartData = event.detail.cart;

    // Re-entrancy guard: updateComponentWithCartData -> removeInvalidDiscounts POSTs
    // /cart/update.js, which re-fires cart:updated. Ignore re-entrant events so the
    // strip doesn't loop. It also self-terminates: once codes are stripped, the next
    // pass finds nothing to strip and makes no POST.
    if (this._isHandlingCartChange) return;
    this._isHandlingCartChange = true;

    // Re-run the existing validate -> strip-non-applicable -> re-render-pills cycle so
    // externally-driven changes (e.g. line-item removal) drop now-stale codes.
    this.updateComponentWithCartData(event.detail.cart).finally(() => {
      this._isHandlingCartChange = false;
    });
  }

  getExistingDiscounts() {
    const discountCodes = [];
    // Active pills only. A stale (inactive) code must not be re-posted with a
    // new one — Shopify replaces the cart's code set with what's posted, so
    // applying a fresh code drops the stale one, same as before stale pills
    // were rendered.
    const discountPills = this.querySelectorAll('.cart-discount__pill:not(.cart-discount__pill--inactive)');
    
    console.log('Found discount pills:', discountPills.length);
    
    discountPills.forEach(pill => {
      const code = pill.dataset.discountCode;
      if (code) {
        discountCodes.push(code);
      }
    });

    console.log('Existing discount codes:', discountCodes);
    return discountCodes;
  }
}

// Register the custom element
if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
